<template>
	<div class="relative">
		<!-- Wishlist -->
		<button
			v-if="type === 'SimpleProduct'"
			type="button"
			class="group absolute top-0 right-0 z-20 outline-hidden"
			:title="tg('aria.add-to-wishlist')"
			@click.stop="addToWishlist"
		>
			<v-icon
				icon="heart"
				class="m-xs size-[18px] group-hover:fill-red-500 group-focus:fill-red-500 group-active:fill-red-600 md:size-md"
			/>
		</button>

		<nuxt-link
			:to="url"
			class="group"
			:aria-label="tg('accessibility.open-product', {product: product.title})"
			:title="tg('accessibility.open-product', {product: product.title})"
			@mouseenter="prepareHoverImage"
			@focus="prepareHoverImage"
			@click="onClick($event, onProductSelect)"
		>
			<!-- Relative wrapper so that the fading-out primary image can anchor to it -->
			<div class="relative">
				<!-- Animate the fading out primary image when image type gets changed
				(no more pointer events, absolutely positioned). Fading is done in the
				leave callback -->
				<transition
					leave-active-class="pointer-events-none absolute! inset-0 z-20"
					@leave="fadeOutPreviousImage"
				>
					<div
						:key="imageRenderKey"
						class="relative flex aspect-square items-center justify-center rounded bg-white"
						:class="{
							'p-sm md:p-sm': imageType === 'cutout',
							'overflow-hidden': imageType === 'model',
						}"
					>
						<v-generic-sale-tag
							v-if="promotion !== undefined"
							class="z-10"
							:promotion="promotion"
							theme="black"
							:price="price"
							:original-price="originalPrice ?? price"
							:club-price="clubPrice"
							:can-show-club-price="canShowClubPrice"
							is-used-in-search-page
						/>

						<div
							class="absolute inset-0 rounded"
							:class="{
								'bg-gray-500/10 opacity-0 transition-opacity duration-300 group-hover:opacity-100':
									imageType === 'model',
								'bg-gray-500/5 group-hover:bg-gray-500/10':
									imageType === 'cutout',
							}"
						></div>

						<!-- Primary image -->
						<nuxt-picture
							v-if="image"
							:key="image.url"
							:src="image.url"
							:alt="product.title || ''"
							loading="lazy"
							:width="image.width"
							:height="image.height"
							:img-attrs="{
								class: `w-full h-full ${
									imageType === 'cutout'
										? 'object-contain py-2'
										: 'object-cover'
								}`,
							}"
							class="aspect-square"
							:class="{
								// eslint-disable-next-line max-len
								'size-full transition-opacity duration-300 ease-in-out group-hover:opacity-0':
									isHoverImageReady,
							}"
							:sizes="
								imageType === 'cutout'
									? 'xs:50vw sm:252px md:232px lg:285px xl:244px 2xl:308px'
									: 'xs:50vw sm:296px md:376px lg:244px xl:308px 2xl:372px'
							"
							@load="isPrimaryImageLoaded = true"
						/>

						<!-- Secondary hover image -->
						<nuxt-picture
							v-if="shouldRenderHoverImage && useHoverImage && hoverImage"
							:key="hoverImage.url"
							:src="hoverImage.url"
							:alt="product.title || ''"
							loading="lazy"
							:width="hoverImage.width"
							:height="hoverImage.height"
							:img-attrs="{
								class: `w-full h-full ${
									hoverImageType === 'cutout'
										? 'object-contain py-2 p-md'
										: 'object-cover'
								}`,
							}"
							class="absolute inset-0 opacity-0 mix-blend-multiply transition-opacity duration-300 ease-in-out"
							:class="{
								'group-hover:opacity-100': isHoverImageReady,
							}"
							:sizes="
								hoverImageType === 'cutout' && isSearch
									? 'xs:50vw sm:252px md:285px lg:180px xl:244px 2xl:308px'
									: 'xs:50vw sm:296px md:376px lg:244px xl:308px 2xl:372px'
							"
							@load="isHoverImageReady = true"
						/>

						<!-- Brand logo -->
						<div
							class="absolute bottom-xs left-0 scrollbar-none flex flex-row items-end gap-x-2 gap-y-1 overflow-auto px-xs md:gap-y-2"
						>
							<nuxt-picture
								v-if="brandLogo"
								class="block size-8 lg:size-12"
								:alt="brandTitle || brand"
								:src="brandLogo"
								loading="lazy"
								:width="48"
								:img-attrs="{
									class: 'size-8 object-contain object-left-bottom lg:size-12',
								}"
								sizes="xs:32px sm:32px md:32px lg:48px xl:48px 2xl:48px"
							>
							</nuxt-picture>
						</div>

						<!-- Tuotekortin tagit -->
						<div
							class="absolute right-0 bottom-xs scrollbar-none flex flex-row items-end gap-x-2 gap-y-1 overflow-auto px-xs md:gap-y-2"
						>
							<template v-for="badge in textProductBadges" :key="badge.id">
								<v-product-card-badge
									v-if="badge.listingTitle && badge.listingTitle.length > 0"
									:badge="badge"
								>
									{{ badge.listingTitle }}
								</v-product-card-badge>
							</template>
						</div>
					</div>
				</transition>

				<div class="p-2">
					<div class="flex flex-row justify-between group-hover:underline">
						<v-subtitle
							class="truncate leading-4 font-bold tracking-xl md:text-base md:leading-5"
							size="sm"
						>
							{{ title }}
						</v-subtitle>
					</div>

					<p class="truncate font-sans text-sm md:text-base">
						{{ specification }}
					</p>

					<div v-if="originalPrice" class="flex items-baseline space-x-2">
						<template v-if="canShowClubPrice && clubPrice">
							<p
								class="font-prenton-cond text-lg leading-5 tracking-xl text-red-600 md:text-3xl md:leading-9"
							>
								<span v-if="type === 'ConfigurableProduct'" class="text-base">
									{{ t("product-part-payment-prefix") }}
								</span>

								{{ formatCurrency(clubPrice) }}
							</p>
						</template>

						<template v-else>
							<p
								class="font-prenton-cond text-lg leading-5 tracking-xl text-red-600 md:text-3xl md:leading-9"
							>
								<span v-if="type === 'ConfigurableProduct'" class="text-base">
									{{ t("product-part-payment-prefix") }}
								</span>

								{{ formatCurrency(price) }}
							</p>

							<!-- Note that we want to treat text-type campaigns as never having a discount, so explicitly disable showing any possible earlier lower price. HOWEVER, we don't want to do this in Estonia since they're using text-style campaigns for promoted products too. -->
							<p
								v-if="
									price < originalPrice &&
									promotion?.style.type !== 'OMNIBUS' &&
									(promotion?.style.type !== 'TEXT' || locale === 'et')
								"
								class="font-sans text-xs tracking-xl text-[#5A5454] line-through md:text-sm"
							>
								{{ formatCurrency(originalPrice) }}
							</p>
						</template>

						<!-- Desktop ratings-->
						<div
							v-if="rating"
							class="hidden flex-1 items-center justify-end space-x-px self-center pt-2 text-xs md:text-sm lg:flex"
						>
							<template v-for="i in Math.ceil(rating)" :key="i">
								<v-icon
									v-if="i - 0.5 <= rating"
									class="fill-[#f6ca6c] stroke-brand-black"
									:icon="getIcon(i, rating)"
								/>
							</template>
						</div>
					</div>

					<!-- Mobile ratings -->
					<div
						v-if="rating"
						class="flex flex-1 items-center justify-start space-x-px self-center pt-2 text-xs md:text-sm lg:hidden"
					>
						<template v-for="i in Math.ceil(rating)" :key="i">
							<v-icon
								v-if="i - 0.5 <= rating"
								class="fill-[#f6ca6c] stroke-brand-black"
								:icon="getIcon(i, rating)"
							/>
						</template>
					</div>

					<v-elevate-inventory-status-badge :product="product" />

					<!-- Single color -->
					<div
						v-if="colorOptions && colorOptions.length > 1"
						class="flex justify-start gap-1 pt-3"
						:title="t('colors')"
						aria-hidden="true"
					>
						<v-color-option
							v-for="color in colorOptions"
							:key="color"
							:color="color"
						/>

						<v-icon v-if="hasMoreColors" icon="add" class="-m-0.5" />
					</div>

					<!-- Multicolor -->
					<div
						v-if="product.swatch && product.swatch.type === 'MULTI'"
						class="flex justify-start gap-1 pt-3"
						:title="t('colors')"
						aria-hidden="true"
					>
						<v-color-option color="MULTI" is-multicolor />
					</div>
				</div>
			</div>
		</nuxt-link>
	</div>
</template>

<script lang="ts" setup>
import type {Product} from "@apptus/esales-api";
import {capitalCase} from "case-anything";
import {promiseTimeout, until} from "@vueuse/core";
import {useGtm} from "@gtm-support/vue-gtm";
import {useToast} from "@pkg/shared/store/toast";
import type {PromotionData, ElevateProductBadge} from "@pkg/shared/types";
import {appleMobileRegex} from "@pkg/shared/helpers/user-agents";

import {useCart} from "@pkg/shared/composables/use-cart";
import {useCurrency} from "@pkg/shared/composables/use-currency";
import {useCustomerLogin} from "@pkg/shared/composables/use-customer-login";
import {useElevate} from "@pkg/shared/composables/use-elevate";
import {useElevateProduct} from "@pkg/shared/composables/use-elevate-product";
import {useGaProduct} from "@pkg/shared/composables/use-ga-product";
import {usePreferredProductImageType} from "@pkg/shared/composables/use-preferred-product-image-type";
import {useReviews} from "@pkg/shared/composables/use-reviews";

// NOTE: Also see `elevate-header-product-card.vue`!

const properties = defineProps<{
	product: Product;
	isSearch?: boolean;
}>();

const maxColorsToShow = 5;

const wishlist = useCart("wishlist");
const toast = useToast();
const {t, locale} = useI18n();
const {t: tg} = useI18n({useScope: "global"});
const {$eventbus} = useNuxtApp();
const {notifyClick} = useElevate();
const gtm = useGtm();

const {$i18n} = useNuxtApp();
const currentLang = computed(() => $i18n.locale.value);

const {getStringValues} = useElevateProduct(properties.product);
const {format: formatCurrency} = useCurrency();
const {hasProfile} = useCustomerLogin();
const {getIcon} = useReviews();

const {gaProduct, onSelect: onProductSelect} = useGaProduct({
	product: properties.product,
});

// Get the user's preferred image type from global state
const preferredImageType = usePreferredProductImageType();

/**
 * In built code (NOT running dev server), toggling the preferred image type causes some weird layout shift (and overflowing) with the images.
 * Not sure what causes this, but forcing re-renders for the images seems to fix it.
 */
const imageRenderKey = ref(0);
const shouldRenderHoverImage = ref(false);
const isHoverImageReady = ref(false);
const isPrimaryImageLoaded = ref(false);

watch(preferredImageType, () => {
	imageRenderKey.value += 1;
	shouldRenderHoverImage.value = false;
	isHoverImageReady.value = false;
	isPrimaryImageLoaded.value = false;
});

/**
 * Cross-fade from the previous image to the new one when the preferred image type
 * changes, most notably right after hydration (see `usePreferredProductImageType`). The
 * previous image container is kept on top of the new one until the new image has loaded
 * (or a while has passed, e.g. when lazy-loading delays it), and then faded out.
 */
async function fadeOutPreviousImage(element: Element, done: () => void) {
	await Promise.race([
		until(isPrimaryImageLoaded).toBe(true),
		promiseTimeout(1000),
	]);

	if (!window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
		try {
			await element.animate([{opacity: 1}, {opacity: 0}], {
				duration: 200,
				easing: "ease-in-out",
			}).finished;
		} catch (e) {
			// eslint-disable-next-line no-console
			console.warn(e);
		}
	}

	done();
}

// Select images based on user preference
const images = computed(() => properties.product.imageInfo.images);

// Primary image: find first matching preferred type, or use first available
const primaryImageObj = computed(() => {
	const imgs = images.value;
	return (
		imgs.find((img) => img.custom?.type === preferredImageType.value) ?? imgs[0]
	);
});

const image = computed(() => primaryImageObj.value?.sources[0]);
const imageType = computed(() =>
	primaryImageObj.value?.custom?.type === "model" ? "model" : "cutout",
);

// Hover image: first image matching non-preferred type or undefined (also note that it can't be the same as the primary image in case the primary image isn't of the preferred type either).
const hoverImageObj = computed(() => {
	const imgs = images.value;
	const reverseImageType =
		preferredImageType.value === "model" ? "cutout" : "model";
	return imgs.find(
		(img) =>
			img.custom?.type === reverseImageType && img !== primaryImageObj.value,
	);
});

const hoverImage = computed(() => hoverImageObj.value?.sources[0] ?? undefined);
const hoverImageType = computed(() =>
	hoverImageObj.value?.custom?.type === "model" ? "model" : "cutout",
);

const hasHoverImage = computed(() => hoverImage.value !== undefined);

const isAppleMobile = ref(false);

// This component is used inside our horizontal `scrollable` components a lot. However, there the pure CSS logic that displays the secondary product image on hover actually stops the Safari browser from scrolling the parent `scrollable` component any further as soon as the CSS animation starts! This seems like a complete bug on ios's side, so we disable the hover effect there. We could disable it on android too though, it's not very touch screen friendly, and may be more confusing than useful to android users too (they'll randomly see the secondary image if they start scrolling above a product card).
const useHoverImage = computed(
	() => hasHoverImage.value && !isAppleMobile.value,
);

function prepareHoverImage() {
	if (useHoverImage.value) {
		shouldRenderHoverImage.value = true;
	}
}

const title = computed(() =>
	capitalCase(
		getStringValues("title")[0] ||
			properties.product.title.split(" ", 1)[0] ||
			"",
	),
);
const specification = computed(() =>
	(
		getStringValues("specification")[0] ||
		properties.product.title.split(" ").slice(1).join(" ")
	).toLocaleLowerCase(),
);

const price = computed(() => properties.product.sellingPrice.min);
// If we have info about omnibus lowest price (simple products do, configurable don't), use that as the original price, otherwise use the cheapest variant's lowest price.
// NOTE: We temporarily don't do this in EE, just use the product's list price instead!
const originalPrice = computed(() => {
	if (currentLang.value === "et") {
		return properties.product.listPrice.min;
	}
	if (properties.product.custom?.lowest_price?.[0]) {
		return Number(properties.product.custom?.lowest_price?.[0].id);
	}
	const prices = properties.product.variants
		.map((variant) => variant.custom?.lowest_price?.[0]?.id)
		.filter((value): value is string => !!value)
		.map(Number);

	return prices.length > 0 ? Math.min(...prices) : undefined;
});

const colorOptions = computed(
	/* 2026-03-25: We will prefer to render color options from custom attributes.
	 *  Enable fallback to 'swatch' colors in case the product does not yet have the attribute (it has not been updated yet)
	 * TODO: Remove the unnessary 'swatch' fallback when all products have been updated
	 */
	() =>
		properties.product.custom?.colorOptions
			? (properties.product.custom?.colorOptions?.map((o) => o.id) || []).slice(
					0,
					maxColorsToShow,
				)
			: properties.product.swatch.colors,
);

const hasMoreColors = computed(
	() =>
		(properties.product.custom?.colorOptions?.length ?? 0) > maxColorsToShow,
);

const type = computed(() => getStringValues("type")[0]);
const productId = computed(() => getStringValues("id")[0]);
const rating = computed(() => properties.product.rating);
const url = computed(() => properties.product.link);

const productBadges = computed<ElevateProductBadge[]>(() => {
	const value = getStringValues("productBadges")[0];

	return value ? JSON.parse(value) : [];
});
const textProductBadges = computed<ElevateProductBadge[]>(() =>
	productBadges.value.filter((badge) => !badge.icon),
);

const promotion = computed<PromotionData | undefined>(() => {
	const value = getStringValues("promotion")[0];

	return value ? JSON.parse(value) : undefined;
});

const clubPrice = computed<number | undefined>(() => {
	const customClubPrice = properties.product.custom?.clubPrice?.[0]?.id;

	if (customClubPrice) {
		return Number(customClubPrice);
	}
	const prices = properties.product.variants
		.map((variant) => variant.custom?.clubPrice?.[0]?.id)
		.filter((value): value is string => !!value)
		.map(Number);

	return prices.length > 0 ? Math.min(...prices) : undefined;
});
const canShowClubPrice = computed(() => hasProfile.value && !!clubPrice.value);

const brand = computed(() => properties.product.brand);
const brandTitle = computed(() => getStringValues("brand_title")[0]);
const brandLogo = computed(() => getStringValues("brand_logo")[0]);

async function onClick(event: Event, callback: (event: Event) => void) {
	await notifyClick(properties.product.ticket);

	callback(event);
}

async function addToWishlist() {
	if (!productId.value) {
		return;
	}

	gtm?.trackEvent({
		event: "add_to_wishlist",
		_clear: true,
		ecommerce: {
			currency: "EUR",
			value: gaProduct.value.price,
			items: [gaProduct.value],
		},
	});

	try {
		await wishlist.addProduct(productId.value);

		$eventbus.emit("product-added-to-wishlist", {
			productId: productId.value,
		});
	} catch {
		toast.add("warning", tg("toast.wishlist-warning"));
	}
}

onMounted(() => {
	isAppleMobile.value = appleMobileRegex.test(navigator.userAgent);
});
</script>

<i18n lang="json">
{
	"et": {
		"quick-delivery": "Kiire tarne",
		"product-part-payment": "Maksa osade kaupa ",
		"product-part-payment-prefix": "al.",
		"product-part-payment-suffix": "/kuu",
		"colors": "Toote värvivalikud"
	},
	"fi": {
		"quick-delivery": "Nopea toimitus",
		"product-part-payment": "Tai maksa erissä ",
		"product-part-payment-prefix": "alk.",
		"product-part-payment-suffix": "/kk",
		"colors": "Tuotteen värivaihtoehdot"
	}
}
</i18n>
